// src/lib/api/team/permissions.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired } from '../shared/validation'

/**
 * Check if user has specific permission
 * Migrated from supabase.js lines 173-190
 */
const _hasPermission = async (permission) => {
  validateRequired(permission, 'Permission')
  
  try {
    const { data: hasAccess, error } = await supabase.rpc('has_permission', {
      p_permission: permission
    })
    
    if (error) {
      console.error('[hasPermission] Error checking permission:', error)
      // Fallback: if function doesn't exist, return true for now
      if (error.code === '42883' || error.message.includes('function') || error.message.includes('does not exist')) {
        console.warn('[hasPermission] Function not found, using fallback (allowing access)')
        return true
      }
      return false
    }
    
    return hasAccess || false
  } catch (error) {
    console.error('[hasPermission] Error during permission check:', error)
    // Fallback: allow access if there's an error
    return true
  }
}

/**
 * Get user permissions
 */
const _getUserPermissions = async (userId = null) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const targetUserId = userId || await getValidatedUserId()
  
  if (!targetUserId) {
    throw new Error('User ID is required')
  }
  
  const { data, error } = await supabase
    .from('user_access_cache')
    .select('permissions, role_type, is_owner')
    .eq('user_id', targetUserId)
    .eq('company_id', companyId)
    .eq('is_active', true)
    .single()
  
  if (error) {
    if (error.code === 'PGRST116') {
      return createSuccessResponse([])
    }
    throw error
  }
  
  return createSuccessResponse(data?.permissions || [])
}

/**
 * Update user permissions
 */
const _updateUserPermissions = async (userId, permissions) => {
  validateRequired(userId, 'User ID')
  validateRequired(permissions, 'Permissions')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('user_access_cache')
    .update({ 
      permissions,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Get user role
 */
const _getUserRole = async (userId = null) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const targetUserId = userId || await getValidatedUserId()
  
  if (!targetUserId) {
    throw new Error('User ID is required')
  }
  
  const { data, error } = await supabase
    .from('user_access_cache')
    .select('role_type, is_owner')
    .eq('user_id', targetUserId)
    .eq('company_id', companyId)
    .eq('is_active', true)
    .single()
  
  if (error) {
    if (error.code === 'PGRST116') {
      return createSuccessResponse(null)
    }
    throw error
  }
  
  return createSuccessResponse(data)
}

/**
 * Update user role
 */
const _updateUserRole = async (userId, role) => {
  validateRequired(userId, 'User ID')
  validateRequired(role, 'Role')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('user_access_cache')
    .update({ 
      role_type: role,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Check if user is owner
 */
const _isOwner = async (userId = null) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const targetUserId = userId || await getValidatedUserId()
  
  if (!targetUserId) {
    throw new Error('User ID is required')
  }
  
  const { data, error } = await supabase
    .from('user_access_cache')
    .select('is_owner')
    .eq('user_id', targetUserId)
    .eq('company_id', companyId)
    .eq('is_active', true)
    .single()
  
  if (error) {
    if (error.code === 'PGRST116') {
      return false
    }
    throw error
  }
  
  return data?.is_owner || false
}

/**
 * Get all users with their permissions for a company
 */
const _getCompanyUsers = async (companyId = null) => {
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }
  
  const { data, error } = await supabase
    .from('user_access_cache')
    .select(`
      user_id,
      role_type,
      is_owner,
      is_active,
      permissions,
      created_at,
      updated_at,
      users(
        id,
        email,
        user_metadata
      )
    `)
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Add employee with permissions
 * Migrated from supabase.js lines 295-310
 */
const _addEmployee = async (userId, firstName, lastName, email, phone = null, role = null, permissions = []) => {
  validateRequired(userId, 'User ID')
  validateRequired(firstName, 'First name')
  validateRequired(lastName, 'Last name')
  validateRequired(email, 'Email')
  
  try {
    const { data: employeeId, error } = await supabase.rpc('add_employee', {
      p_user_id: userId,
      p_first_name: firstName,
      p_last_name: lastName,
      p_email: email,
      p_phone: phone,
      p_role: role,
      p_permissions: permissions
    })
    
    if (error) {
      console.error('[addEmployee] Error adding employee:', error)
      throw error
    }
    
    console.log('[addEmployee] Employee added successfully:', employeeId)
    return employeeId
  } catch (error) {
    console.error('[addEmployee] Error during employee creation:', error)
    throw error
  }
}

/**
 * Remove user from company
 */
const _removeUserFromCompany = async (userId) => {
  validateRequired(userId, 'User ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('user_access_cache')
    .update({ 
      is_active: false,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', userId)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true }
}

// Export wrapped functions with error handling
export const hasPermission = withErrorHandling(_hasPermission, 'check permission', 'permission')
export const getUserPermissions = withErrorHandling(_getUserPermissions, 'get user permissions', 'permissions')
export const updateUserPermissions = withErrorHandling(_updateUserPermissions, 'update user permissions', 'permissions')
export const getUserRole = withErrorHandling(_getUserRole, 'get user role', 'role')
export const updateUserRole = withErrorHandling(_updateUserRole, 'update user role', 'role')
export const isOwner = withErrorHandling(_isOwner, 'check if owner', 'ownership')
export const getCompanyUsers = withErrorHandling(_getCompanyUsers, 'get company users', 'users')
export const addEmployee = withErrorHandling(_addEmployee, 'add employee', 'employee')
export const removeUserFromCompany = withErrorHandling(_removeUserFromCompany, 'remove user from company', 'user')